/**
 * V278.0: Cloudinary Upload Guard
 * 
 * Prevents placeholder images from being uploaded to Cloudinary.
 * Placeholders should ONLY be rendered client-side, never stored in Cloudinary.
 * This saves storage space and prevents data pollution.
 * 
 * BLOCKED SOURCES:
 * - data:image/* URLs (canvas-generated placeholders)
 * - data:image/svg+xml URLs (SVG placeholders)
 * - UI Avatars service URLs
 * - Dicebear avatar URLs
 * - Empty/null/undefined sources
 * 
 * ALLOWED SOURCES:
 * - http:// and https:// URLs (real images)
 * - blob: URLs (user-selected files)
 * - Google OAuth photos (googleusercontent.com)
 */

/**
 * Check if a source is a placeholder image
 */
export function isPlaceholderImage(source: string | null | undefined): boolean {
  if (!source) return true;  // No source = placeholder needed
  
  // Data URLs are canvas-generated or SVG placeholders
  if (source.startsWith('data:image/')) {
    console.warn('[V278.0] ⚠️ Placeholder detected: data:image/* URL');
    return true;
  }
  
  // SVG placeholders
  if (source.includes('data:image/svg+xml')) {
    console.warn('[V278.0] ⚠️ Placeholder detected: SVG data URL');
    return true;
  }
  
  // UI Avatars fallback service
  if (source.includes('ui-avatars.com')) {
    console.warn('[V278.0] ⚠️ Placeholder detected: UI Avatars service');
    return true;
  }
  
  // Dicebear avatars
  if (source.includes('dicebear.com')) {
    console.warn('[V278.0] ⚠️ Placeholder detected: Dicebear service');
    return true;
  }
  
  return false;
}

/**
 * Check if a source should be uploaded to Cloudinary
 * 
 * @param source - Image source URL or data URL
 * @returns true if source is a real image that should be uploaded, false otherwise
 */
export function shouldUploadToCloudinary(source: string | null | undefined): boolean {
  if (!source) {
    console.warn('[V278.0] ⚠️ Blocked: No source provided');
    return false;
  }
  
  // Block placeholders
  if (isPlaceholderImage(source)) {
    console.warn('[V278.0] ⚠️ Blocked: Placeholder image detected, not uploading to Cloudinary');
    return false;
  }
  
  // Must be a valid URL (http/https/blob)
  const isValidUrl = source.startsWith('http://') || 
                     source.startsWith('https://') || 
                     source.startsWith('blob:');
  
  if (!isValidUrl) {
    console.warn('[V278.0] ⚠️ Blocked: Invalid URL format (not http/https/blob)');
    return false;
  }
  
  // Additional validation: Check if it's a real Google OAuth photo
  if (source.includes('googleusercontent.com')) {
    console.log('[V278.0] ✅ Valid: Google OAuth photo detected');
    return true;
  }
  
  // Additional validation: Check if it's already a Cloudinary URL (re-upload)
  if (source.includes('res.cloudinary.com')) {
    console.log('[V278.0] ✅ Valid: Cloudinary URL (re-upload)');
    return true;
  }
  
  // Additional validation: Blob URLs are user-selected files
  if (source.startsWith('blob:')) {
    console.log('[V278.0] ✅ Valid: Blob URL (user-selected file)');
    return true;
  }
  
  // Other HTTP/HTTPS URLs - allow but log
  console.log('[V278.0] ✅ Valid: HTTP/HTTPS URL');
  return true;
}

/**
 * Validate that a File object is a real image, not a placeholder
 * 
 * @param file - File object to validate
 * @returns true if file is a real image, false if it's a placeholder
 */
export function isRealImageFile(file: File): boolean {
  // Check file type
  if (!file.type.startsWith('image/')) {
    console.warn('[V278.0] ⚠️ Blocked: File is not an image type');
    return false;
  }
  
  // Block SVG files (placeholders)
  if (file.type === 'image/svg+xml') {
    console.warn('[V278.0] ⚠️ Blocked: SVG file (placeholder)');
    return false;
  }
  
  // Check file size (placeholders are usually very small)
  if (file.size < 1000) {  // Less than 1KB is suspicious
    console.warn('[V278.0] ⚠️ Blocked: File too small (< 1KB, likely placeholder)');
    return false;
  }
  
  // Check filename patterns
  if (file.name.includes('placeholder') || 
      file.name.includes('fallback') || 
      file.name.includes('default-avatar')) {
    console.warn('[V278.0] ⚠️ Blocked: Filename indicates placeholder');
    return false;
  }
  
  console.log('[V278.0] ✅ Valid: Real image file');
  return true;
}

/**
 * Comprehensive validation before Cloudinary upload
 * 
 * @param source - Image source (URL or File)
 * @returns true if should upload, false otherwise
 */
export function validateBeforeCloudinaryUpload(source: string | File | null | undefined): boolean {
  if (!source) {
    console.warn('[V278.0] ⚠️ Upload blocked: No source provided');
    return false;
  }
  
  // Handle File objects
  if (source instanceof File) {
    const isValid = isRealImageFile(source);
    if (!isValid) {
      console.warn('[V278.0] ⚠️ Upload blocked: File validation failed');
    }
    return isValid;
  }
  
  // Handle URL strings
  if (typeof source === 'string') {
    const isValid = shouldUploadToCloudinary(source);
    if (!isValid) {
      console.warn('[V278.0] ⚠️ Upload blocked: URL validation failed');
    }
    return isValid;
  }
  
  console.warn('[V278.0] ⚠️ Upload blocked: Unknown source type');
  return false;
}

